/**
 * Standalone Team Chat Page
 * Path: /teams/[teamId]/chat/[id] - for specific chat
 * Path: /teams/[teamId]/chat/new - for new chat
 *
 * This is a standalone chat interface for team conversations,
 * adapted from the agent chat page for teams.
 */

import { useConditionalAuth } from '@/components/auth/conditional-auth-provider'
import { Chat } from '@/components/chat/chat'
import { InteractiveChat } from '@/components/chat/interactive'
import { DomainNotFoundPage } from '@/components/domain-not-found-page'
import { useChatEvents } from '@/hooks/use-chat-events'
import { usePageTitle } from '@/hooks/use-page-title'
import { useTeam } from '@/hooks/use-teams'
import { chatService } from '@/services/chat-service'
import type { ChatMessage } from '@/types/chat-message'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'

interface ChatData {
  id: string
  title: string
  sandboxId?: string
  metadata?: Record<string, unknown>
}

/**
 * Narrow error shape used by chat loading flows.
 * Some transport helpers attach status metadata outside the standard Error type.
 */
interface ChatLoadErrorShape {
  /** Top-level HTTP-like status code exposed by the request helper. */
  status?: number
  /** Nested response object when the fetch wrapper preserves raw response data. */
  response?: {
    /** HTTP status code reported by the underlying response. */
    status?: number
  }
  /** Fallback numeric code used by some legacy callers. */
  code?: number
}

/**
 * Pattern info for display - matches sidebar
 */
const PATTERN_INFO = {
  hierarchical: { label: "Hierarchical", icon: "🔗" },
  pipeline: { label: "Pipeline", icon: "🔄" },
  parallel: { label: "Parallel", icon: "⚡" },
  review: { label: "Review", icon: "👁️" },
  debate: { label: "Debate", icon: "💬" },
} as const;

/**
 * Detects whether a chat load failure maps to a 404-style missing chat response.
 * Keeps the redirect logic typed without relying on `any`.
 */
function isMissingChatError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false
  }

  const chatError = error as ChatLoadErrorShape
  return (
    chatError.status === 404
    || chatError.response?.status === 404
    || chatError.code === 404
  )
}

export function StandaloneTeamChatPage() {
  const navigate = useNavigate()
  const chatEvents = useChatEvents()
  const { teamId, id } = useParams<{ teamId: string; id: string }>()

  // Get conditional auth context to determine mode
  const { isEmbedMode, isBubbleMode, isAuthenticated } = useConditionalAuth()

  const { team, loading: teamLoading, error: teamError } = useTeam(teamId || null)

  const [chat, setChat] = useState<ChatData | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Determine if this is a new chat or existing chat
  const isNewChat = id === 'new'

  // Generate a stable chat ID for new chats
  const [newChatId] = useState(() => uuidv4())
  // For new chats, use the generated UUID. For existing chats, use the URL param.
  // This ensures the Chat component always has a valid ID during streaming.
  const chatId = isNewChat ? newChatId : id

  // Pattern badge text
  const patternBadge = team ? `${PATTERN_INFO[team.pattern]?.icon} ${PATTERN_INFO[team.pattern]?.label}` : ''

  const pageTitle = usePageTitle(
    team ? `${team.name} (${patternBadge}) - ${chat?.title || 'New Chat'}` : 'Team Chat',
    'Chat with your AI team'
  )

  // Load existing chat data (if not new chat and not bubble mode)
  useEffect(() => {
    console.log('[TeamChatPage] useEffect triggered:', {
      urlId: id,
      isNewChat,
      computedChatId: chatId,
      newChatId
    })

    if (!teamId) {
      setError('Team ID is required')
      setLoading(false)
      return
    }

    // Wait for authentication to complete
    if (isEmbedMode && !isAuthenticated) {
      setLoading(true)
      return
    }

    if (!isEmbedMode && !isAuthenticated) {
      setLoading(true)
      return
    }

    // In bubble mode, always treat as new chat
    if (isNewChat || isBubbleMode) {
      setChat(null)
      setMessages([])
      setError(null)
      setLoading(false)
      return
    }

    if (!id) {
      setError('Chat ID is required')
      setLoading(false)
      return
    }

    // Extra check: if we're on /chat/new or using temporary newChatId, skip loading
    if (id === 'new' || id === newChatId) {
      console.log('[TeamChatPage] Skipping load for:', { id, newChatId, reason: 'new or temporary chat' })
      setChat(null)
      setMessages([])
      setError(null)
      setLoading(false)
      return
    }

    const loadChatData = async () => {
      console.log('[TeamChatPage] loadChatData called with:', {
        useParams_id: id,
        isNewChat,
        computedChatId: chatId,
        newChatId,
        url: window.location.pathname
      })
      try {
        setLoading(true)
        setError(null)

        // First fetch chat details
        console.log('[TeamChatPage] Calling getTeamChatById with:', { teamId, chatId: id })
        const chatResponse = await chatService.getTeamChatById(teamId, id)

        if (!chatResponse.success || !chatResponse.data) {
          setError('Chat not found')
          setLoading(false)
          return
        }

        const chatData: ChatData = {
          id: chatResponse.data.id,
          title: chatResponse.data.title,
          sandboxId: chatResponse.data.sandboxId,
          metadata: chatResponse.data.metadata as Record<string, unknown> | undefined,
        }

        setChat(chatData)

        // Then fetch messages (only if chat fetch succeeded)
        try {
          const messagesResponse = await chatService.getTeamChatMessages(teamId, id)
          // Ensure messages is always an array
          const msgs = messagesResponse.success ? (Array.isArray(messagesResponse.data) ? messagesResponse.data : []) : []
          setMessages(msgs)
        } catch (msgErr) {
          console.error('Failed to load messages, but continuing with empty array:', msgErr)
          setMessages([])
        }
      } catch (err: unknown) {
        console.error('Failed to load chat data:', err)
        if (isMissingChatError(err)) {
          window.location.replace(`/teams/${teamId}/chat/new`)
        } else {
          setError('Failed to load chat')
        }
      } finally {
        setLoading(false)
      }
    }

    loadChatData()
  }, [teamId, id, isNewChat, isBubbleMode, isEmbedMode, isAuthenticated, chatId, newChatId])

  // Listen for chat deletion events
  useEffect(() => {
    if (!id || isNewChat || isBubbleMode) return

    const unsubscribe = chatEvents.subscribe("delete-chat", (deletedChatId) => {
      if (deletedChatId === id) {
        navigate(`/teams/${teamId}/chat/new`, { replace: true })
      }
    })

    return unsubscribe
  }, [id, isNewChat, isBubbleMode, teamId, navigate, chatEvents])

  /**
   * Handle new chat creation after first streaming response completes.
   * The client-generated UUID is used as the DB primary key, so the chat
   * already exists in the database by the time this fires.
   */
  const handleNewChat = useCallback((newChatId: string) => {
    console.log('[TeamChatPage] handleNewChat called with:', { newChatId, teamId, isBubbleMode })

    // In bubble mode, don't navigate to preserve embed parameters
    if (isBubbleMode) {
      setChat({ id: newChatId, title: `Chat with ${team?.name || 'Team'}` })
      return
    }

    // For normal and embed modes, navigate to the actual chat URL
    // At this point, the chat exists in the database (created by TeamAgent during streaming)
    console.log(`[TeamChatPage] Navigating to: /teams/${teamId}/chat/${newChatId}`)
    navigate(`/teams/${teamId}/chat/${newChatId}`, { replace: true })
  }, [teamId, navigate, isBubbleMode, team])  // Removed isNewChat from dependencies

  /** Keeps the standalone page title in sync after inline title renames. */
  const handleTitleUpdated = useCallback((nextTitle: string) => {
    setChat((currentChat) => {
      if (!currentChat) {
        return currentChat
      }

      return {
        ...currentChat,
        title: nextTitle,
      }
    })
  }, [])

  // Loading state
  if (loading || teamLoading) {
    return (
      <>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading team chat...</p>
          </div>
        </div>
      </>
    )
  }

  // Error state
  if (error || teamError) {
    return (
      <>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-destructive mb-4">{error || teamError}</p>
            <button
              onClick={() => navigate(`/teams/${teamId}/chat/new`)}
              className="text-primary hover:underline"
            >
              Start a new chat
            </button>
          </div>
        </div>
      </>
    )
  }

  // Domain not found state (for published agents with domain restrictions)
  if (team?.published && team?.domainRestrictionEnabled) {
    const currentDomain = window.location.hostname
    const allowedDomains = team.allowedDomains || []

    if (
      allowedDomains.length > 0 &&
      !allowedDomains.includes(currentDomain)
    ) {
      return <DomainNotFoundPage />
    }
  }

  // Render chat
  return (
    <>
      {pageTitle}
      {team?.interactiveChat ? (
        <InteractiveChat
          key={`${teamId}-${chatId}-${isBubbleMode ? 'bubble' : 'normal'}`}
          team={team}
          chatProps={{
            id: chatId,
            title: isBubbleMode ? "" : (chat?.title || "New Chat"),
            initialMessages: isNewChat || isBubbleMode ? [] : messages,
            autoResume: !isNewChat && chat?.metadata?.status === 'streaming',
            sandboxId: chat?.sandboxId,
            teamId,
            teamInfo: team,
            isReadonly: false,
            onNewChat: isNewChat || isBubbleMode ? handleNewChat : undefined,
            onTitleUpdated: handleTitleUpdated,
          }}
        />
      ) : (
        <Chat
          key={`${teamId}-${chatId}-${isBubbleMode ? 'bubble' : 'normal'}`}
          id={chatId}
          title={isBubbleMode ? "" : (chat?.title || "New Chat")}
          initialMessages={isNewChat || isBubbleMode ? [] : messages}
          autoResume={!isNewChat && chat?.metadata?.status === 'streaming'}
          sandboxId={chat?.sandboxId}
          teamId={teamId}
          teamInfo={team || undefined}
          isReadonly={false}
          onNewChat={isNewChat || isBubbleMode ? handleNewChat : undefined}
          onTitleUpdated={handleTitleUpdated}
        />
      )}
    </>
  )
}
